import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox } from 'antd';
import { Field } from 'redux-form';
import { CircularProgress } from '@mui/material';
import { S3Client, PutObjectCommand } from "@aws-sdk/client-s3";
import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-providers";
import { notification } from 'antd';

import { customInput } from '../../../../../common/custom-input';
import { customTextarea } from '../../../../../common/customTextarea';
import { getStorage } from '../../../../../../utils/common';
import { ADMIN_DETAILS, JWT_ID_TOKEN } from '../../../../../../dataProvider/constant';
import { isEditablePreStartQuestion, PRE_START_PHOTO_UPLOAD_PATH } from '../jobShiftPreStartFormUtils';
import { formatAnswerValue, resolveParticipantName } from "../jobShiftPreStartUtils";
import styles from "../JobShiftPreStartView.module.css";

const getFlagOnValue = (questionConfig = {}) => {
  const responseConfig = questionConfig && questionConfig.response_config ? questionConfig.response_config : {};
  return responseConfig.flagOnValue !== undefined && responseConfig.flagOnValue !== null ? `${responseConfig.flagOnValue}` : "";
};

const hasCommentValue = (answer = null) => Boolean(answer && typeof answer.comment === 'string' && answer.comment.trim() !== '');

const getCommentEntryKey = (participantIndex) => `comment-${participantIndex}`;

const getRadioOptions = (questionConfig = {}) => {
  const responseConfig = questionConfig && questionConfig.response_config ? questionConfig.response_config : {};
  return Array.isArray(responseConfig.options) ? responseConfig.options : [];
};

const getRadioOptionStateClass = (optionValue, isChecked) => {
  if (!isChecked) {
    if (["yes", "pass"].includes(optionValue)) {
      return styles.PreStartOptionItemPositive;
    }
    if (["no", "fail", "not_applicable"].includes(optionValue)) {
      return styles.PreStartOptionItemNegative;
    }
    return "";
  }

  if (["yes", "pass"].includes(optionValue)) {
    return styles.PreStartOptionItemSelectedPositive;
  }

  if (["no", "fail", "not_applicable"].includes(optionValue)) {
    return styles.PreStartOptionItemSelectedNegative;
  }

  return styles.PreStartOptionItemSelectedPositive;
};

function RadioOptions({ options, selectedValue, onChange }) {
  return (
    <div className={styles.PreStartOptionList}>
      {options.map((option, optionIndex) => {
        const optionValue = option && option.value !== undefined && option.value !== null ? `${option.value}` : "";
        const isChecked = `${selectedValue || ""}` === optionValue;
        const optionStateClass = getRadioOptionStateClass(optionValue, isChecked);

        return (
          <Checkbox checked={isChecked} className={`${styles.PreStartOptionItem} ${optionStateClass}`.trim()} key={`${optionValue}-${optionIndex}`} onChange={() => onChange(optionValue)}>
            {option && option.label ? option.label : optionValue}
          </Checkbox>
        );
      })}
    </div>
  );
}

function EditableRadioField({ input, options, flagFieldName, flagLabel, flagOnValue, formChange, expandFlaggedToFullRow }) {
  const selectedValue = input && input.value ? `${input.value}` : "";
  const handleChange = (optionValue) => {
    if (formChange && optionValue !== selectedValue && flagFieldName) {
      formChange(flagFieldName, "");
    }

    if (input && input.onChange) {
      input.onChange(optionValue);
    }
  };

  const showFlaggedResponseField = Boolean(flagFieldName) && Boolean(flagOnValue) && selectedValue === flagOnValue;

  return (
    <div className={styles.PreStartEditableRadioWrap}>
      <div className={styles.PreStartEditableRadioRow}>
        <RadioOptions options={options} selectedValue={selectedValue} onChange={handleChange} />
      </div>
      {showFlaggedResponseField ? (
        <div className={`${styles.PreStartFlaggedResponseWrap} ${expandFlaggedToFullRow ? styles.PreStartFlaggedResponseWrapFullRow : ''}`.trim()}>
          <div className={styles.PreStartFlaggedResponseSeparator} />
          <div className={styles.PreStartFlaggedResponseLabel}>{flagLabel}</div>
          <Field
            name={flagFieldName}
            component={customTextarea}
            rows={2}
            className={styles.PreStartFlaggedResponseTextarea}
            otherProps={{ style: { resize: 'none' } }}
          />
        </div>
      ) : null}
    </div>
  );
}

function EditableNumberField({ fieldName, unit }) {
  return (
    <div className={styles.PreStartEditableNumberWrap}>
      <div className={styles.PreStartEditableNumberControl}>
        <Field
          name={fieldName}
          component={customInput}
          type="number"
          className={`${styles.PreStartEditableInput} ${styles.PreStartEditableNumberInput}`}
        />
      </div>
      {unit ? <span className={styles.PreStartEditableUnit}>{unit}</span> : null}
    </div>
  );
}

function EditableTextField({ fieldName }) {
  return (
    <div className={styles.PreStartEditableTextareaWrap}>
      <Field
        name={fieldName}
        component={customTextarea}
        rows={2}
        className={styles.PreStartEditableTextarea}
        otherProps={{ style: { resize: 'none' } }}
      />
    </div>
  );
}

function PhotoAttachmentField({ hasPhoto, isUploading, onRemove, onUpload }) {
  return (
    <div className={styles.PreStartAttachmentActionRow}>
      <div className={styles.PreStartAttachmentButtonGroup}>
        <button
          type="button"
          className={`${styles.PreStartAttachmentPill} ${hasPhoto ? styles.PreStartAttachmentPillActive : ''}`.trim()}
          onClick={onUpload}
          disabled={isUploading}
        >
          {isUploading ? <CircularProgress size={14} className={styles.PreStartAttachmentSpinner} /> : hasPhoto ? <Checkbox checked disabled className={styles.PreStartAttachmentCheckbox} /> : (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" height="14px" width="14px">
              <path fill="currentColor" d="M352 173.3L352 384C352 401.7 337.7 416 320 416C302.3 416 288 401.7 288 384L288 173.3L246.6 214.7C234.1 227.2 213.8 227.2 201.3 214.7C188.8 202.2 188.8 181.9 201.3 169.4L297.3 73.4C309.8 60.9 330.1 60.9 342.6 73.4L438.6 169.4C451.1 181.9 451.1 202.2 438.6 214.7C426.1 227.2 405.8 227.2 393.3 214.7L352 173.3zM320 464C364.2 464 400 428.2 400 384L480 384C515.3 384 544 412.7 544 448L544 480C544 515.3 515.3 544 480 544L160 544C124.7 544 96 515.3 96 480L96 448C96 412.7 124.7 384 160 384L240 384C240 428.2 275.8 464 320 464zM464 488C477.3 488 488 477.3 488 464C488 450.7 477.3 440 464 440C450.7 440 440 450.7 440 464C440 477.3 450.7 488 464 488z"/>
            </svg>
        )}
          {isUploading ? 'Upload Photo' : hasPhoto ? 'Photo Attached' : 'Upload Photo'}
          {hasPhoto && !isUploading ? <svg xmlns="http://www.w3.org/2000/svg" height="14px" width="14px" viewBox="0 0 640 640">
            <path fill="currentColor" d="M384 96C384 78.3 398.3 64 416 64L544 64C561.7 64 576 78.3 576 96L576 224C576 241.7 561.7 256 544 256C526.3 256 512 241.7 512 224L512 173.3L374.6 310.6C362.1 323.1 341.8 323.1 329.3 310.6C316.8 298.1 316.8 277.8 329.3 265.3L466.7 128L416 128C398.3 128 384 113.7 384 96zM96 160C96 124.7 124.7 96 160 96L288 96C305.7 96 320 110.3 320 128C320 145.7 305.7 160 288 160L160 160L160 480L480 480L480 352C480 334.3 494.3 320 512 320C529.7 320 544 334.3 544 352L544 480C544 515.3 515.3 544 480 544L160 544C124.7 544 96 515.3 96 480L96 160z" />
          </svg> : null}
        </button>
        {hasPhoto && !isUploading ? (
          <button
            type="button"
            className={styles.PreStartAttachmentRemoveButton}
            onClick={onRemove}
            title="Remove attached photo"
            aria-label="Remove attached photo"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" aria-hidden="true">
              <path d="M232.7 69.9L224 96L128 96C110.3 96 96 110.3 96 128C96 145.7 110.3 160 128 160L512 160C529.7 160 544 145.7 544 128C544 110.3 529.7 96 512 96L416 96L407.3 69.9C402.9 56.8 390.7 48 376.9 48L263.1 48C249.3 48 237.1 56.8 232.7 69.9zM512 208L128 208L149.1 531.1C150.7 556.4 171.7 576 197 576L443 576C468.3 576 489.3 556.4 490.9 531.1L512 208z" />
            </svg>
          </button>
        ) : null}
      </div>
    </div>
  );
}

function PhotoUploadAnswerField({ input, formChange, fieldName, onOpenPhotoPreview, photoPreviewKey }) {
  const fileInputRef = useRef(null);
  const abortControllerRef = useRef(null);
  const inputValue = input && input.value ? input.value : null;
  const isUploading = Boolean(inputValue && inputValue.originFileObj && !inputValue.isUploaded && !inputValue.isFailed);
  const photoValue = useMemo(() => {
    if (!inputValue) {
      return "";
    }

    return formatAnswerValue({ response_config: { type: "photo" } }, { value: inputValue }, "");
  }, [inputValue]);

  useEffect(() => {
    if (!inputValue || !inputValue.originFileObj || inputValue.isUploaded || inputValue.isFailed) {
      return undefined;
    }

    const uploadFile = async () => {
      abortControllerRef.current = new AbortController();

      try {
        const region = process.env.REACT_APP_REGION;
        const logins = {
          [process.env.REACT_APP_COGNITO_USER_POOL_ID]: getStorage(JWT_ID_TOKEN)
        };
        const client = new S3Client({
          region,
          credentials: fromCognitoIdentityPool({
            identityPoolId: process.env.REACT_APP_IDENTITY_POOL_ID,
            client: new CognitoIdentityClient({ region }),
            logins,
            clientConfig: { region }
          })
        });
        const organisation = JSON.parse(getStorage(ADMIN_DETAILS)).organisation;
        const originalName = inputValue.originFileObj.name || "photo";
        const fileName = `${new Date().getTime()}_${originalName}`;
        const relativePath = `${PRE_START_PHOTO_UPLOAD_PATH}/${fileName}`;
        const uploadedValue = {
          file_name: fileName,
          file_type: inputValue.originFileObj.type,
          file_url: relativePath
        };

        await client.send(
          new PutObjectCommand({
            Bucket: process.env.REACT_APP_TEMP_BUCKET,
            Key: `${organisation.slug}/${relativePath}`,
            Body: inputValue.originFileObj,
            ContentType: inputValue.originFileObj.type
          }),
          { abortSignal: abortControllerRef.current.signal }
        );

        if (input && input.onChange) {
          input.onChange(uploadedValue);
        }
      } catch (error) {
        notification.error({
          message: "Failed to upload File.",
          className: "ant-error"
        });

        if (input && input.onChange) {
          input.onChange({
            ...inputValue,
            isUploaded: true,
            isFailed: true
          });
        }
      }
    };

    uploadFile();

    return undefined;
  }, [input, inputValue]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleFileSelection = (event) => {
    const selectedFile = event && event.target && event.target.files && event.target.files[0] ? event.target.files[0] : null;

    if (!selectedFile) {
      return;
    }

    if (!selectedFile.type || !["image/png", "image/jpeg", "image/webp"].includes(selectedFile.type)) {
      notification.error({
        message: "Unsupported File Type",
        description: "Only Images (.jpg, .jpeg, .png, .webp) are accepted.",
        className: "ant-error"
      });
      event.target.value = "";
      return;
    }

    if (input && input.onChange) {
      input.onChange({
        originFileObj: selectedFile,
        visible_name: selectedFile.name,
        type: selectedFile.type,
        isUploaded: false,
        isFailed: false
      });
    }

    event.target.value = "";
  };

  const handleRemovePhoto = () => {
    if (formChange && fieldName) {
      formChange(fieldName, null);
    }
  };

  const handleUploadClick = () => {
    if (photoValue && onOpenPhotoPreview && photoPreviewKey && !isUploading) {
      onOpenPhotoPreview(photoPreviewKey);
      return;
    }

    if (fileInputRef.current && !isUploading) {
      fileInputRef.current.click();
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className={styles.PreStartHiddenFileInput}
        onChange={handleFileSelection}
      />
      <PhotoAttachmentField
        hasPhoto={Boolean(photoValue)}
        isUploading={isUploading}
        onRemove={handleRemovePhoto}
        onUpload={handleUploadClick}
      />
    </>
  );
}

function AnswerCell({ filesPath, questionConfig, answer, fieldName, flaggedResponseFieldName, formChange, expandFlaggedToFullRow = false, photoPreviewKey, onOpenPhotoPreview }) {
  const responseType = questionConfig && questionConfig.response_config ? questionConfig.response_config.type : "";
  const isEditable = Boolean(fieldName) && isEditablePreStartQuestion(questionConfig);

  if (responseType === "radio") {
    if (isEditable) {
      return (
        <Field
          name={fieldName}
          component={EditableRadioField}
          options={getRadioOptions(questionConfig)}
          flagFieldName={flaggedResponseFieldName}
          flagLabel="Flagged"
          flagOnValue={getFlagOnValue(questionConfig)}
          formChange={formChange}
          expandFlaggedToFullRow={expandFlaggedToFullRow}
        />
      );
    }

    return <RadioOptions options={getRadioOptions(questionConfig)} selectedValue={answer && answer.value} onChange={() => {}} />;
  }

  if (responseType === "number" && isEditable) {
    const unit = questionConfig && questionConfig.response_config && typeof questionConfig.response_config.unit === 'string'
      ? questionConfig.response_config.unit.trim()
      : '';

    return <EditableNumberField fieldName={fieldName} unit={unit} />;
  }

  if (responseType === "text" && isEditable) {
    return <EditableTextField fieldName={fieldName} />;
  }

  if (responseType === "signature") {
    const signatureValue = formatAnswerValue(questionConfig, answer, filesPath);

    return signatureValue ? (
      <img className={styles.PreStartSignatureImage} src={signatureValue} alt="Signature" />
    ) : (
      <span className={styles.PreviewValue}>-</span>
    );
  }

  if (responseType === "photo") {
    return (
      <Field
        name={fieldName}
        component={PhotoUploadAnswerField}
        formChange={formChange}
        fieldName={fieldName}
        onOpenPhotoPreview={onOpenPhotoPreview}
        photoPreviewKey={photoPreviewKey}
      />
    );
  }

  const answerText = formatAnswerValue(questionConfig, answer, filesPath);
  return <span className={styles.PreviewValue}>{answerText || "-"}</span>;
}

function CommentIconButton({ active, onClick }) {
  return (
    <button
      type="button"
      className={`${styles.PreStartCommentButton} ${active ? styles.PreStartCommentButtonVisible : ''}`.trim()}
      onClick={onClick}
      title={active ? 'Comment added' : 'Add comment'}
      aria-label={active ? 'Comment added' : 'Add comment'}
    >
      <svg className={styles.PreStartCommentIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" aria-hidden="true">
        <path d="M576 304C576 436.5 461.4 544 320 544C282.9 544 247.7 536.6 215.9 523.3L97.5 574.1C88.1 578.1 77.3 575.8 70.4 568.3C63.5 560.8 62 549.8 66.8 540.8L115.6 448.6C83.2 408.3 64 358.3 64 304C64 171.5 178.6 64 320 64C461.4 64 576 171.5 576 304z" />
      </svg>
    </button>
  );
}

export default function PreStartQuestionView({ question, number, supervisorParticipant, supervisorAnswer, supervisorFieldName, supervisorCommentFieldName, supervisorFlaggedResponseFieldName, workerAnswers = [], formChange, filesPath, onOpenPhotoPreview }) {
  const questionConfig = question || {};
  const answerScope = questionConfig && questionConfig.answer_scope ? questionConfig.answer_scope : "supervisor";
  const isWorkerScope = answerScope === "worker";
  const questionText = questionConfig && questionConfig.question_text ? questionConfig.question_text : "";
  const guidanceText = questionConfig && questionConfig.guidance_text ? questionConfig.guidance_text : "";
  const [openCommentKeys, setOpenCommentKeys] = useState({});
  const workerScopeAnswers = [
    ...(supervisorParticipant ? [{ participant: supervisorParticipant, participantIndex: "supervisor", answer: supervisorAnswer, fieldName: supervisorFieldName, commentFieldName: supervisorCommentFieldName, flaggedResponseFieldName: supervisorFlaggedResponseFieldName }] : []),
    ...workerAnswers
  ];
  const commentEntries = (isWorkerScope ? workerScopeAnswers : [{
    participant: supervisorParticipant,
    participantIndex: 'supervisor',
    answer: supervisorAnswer,
    commentFieldName: supervisorCommentFieldName
  }]).map((entry) => {
    const entryKey = getCommentEntryKey(entry && entry.participantIndex);
    const participantName = resolveParticipantName(entry && entry.participant);
    const hasComment = hasCommentValue(entry && entry.answer);
    const isVisible = Boolean(openCommentKeys[entryKey]) || hasComment;

    return {
      ...entry,
      entryKey,
      participantName,
      hasComment,
      isVisible
    };
  });
  const visibleCommentEntries = commentEntries.filter(({ isVisible, commentFieldName }) => Boolean(isVisible && commentFieldName));

  const showComment = (entryKey) => {
    setOpenCommentKeys((prevState) => ({
      ...prevState,
      [entryKey]: true
    }));
  };

  const hideComment = (entryKey, commentFieldName) => {
    if (formChange && commentFieldName) {
      formChange(commentFieldName, "");
    }

    setOpenCommentKeys((prevState) => {
      const nextState = { ...prevState };
      delete nextState[entryKey];
      return nextState;
    });
  };

  return (
    <tr>
      <td className={styles.PreStartQuestionNumber}>{number}</td>
      <td className={styles.PreStartQuestionTextCell} colSpan={2}>
        <div className={styles.PreStartQuestionTextLine}>
          <span>{questionText}</span>
          {isWorkerScope ? <span className={styles.PreStartQuestionScope}>Per-Worker</span> : null}
        </div>
        {guidanceText ? <div className={styles.PreStartQuestionGuidance}>{guidanceText}</div> : null}
        {visibleCommentEntries.length > 0 ? (
          <div className={styles.PreStartCommentStack}>
            {visibleCommentEntries.map(({ entryKey, participantName, commentFieldName }) => (
              <div className={styles.PreStartCommentCard} key={entryKey}>
                <div className={styles.PreStartCommentHeader}>
                  <div className={styles.PreStartCommentLabel}>{isWorkerScope ? `Comment${participantName ? ` (${participantName})` : ''}` : 'Comment'}</div>
                  <button type="button" className={styles.PreStartCommentRemoveButton} onClick={() => hideComment(entryKey, commentFieldName)}>
                    Remove comment
                  </button>
                </div>
                <Field
                  name={commentFieldName}
                  component={customTextarea}
                  rows={2}
                  className={styles.PreStartCommentTextarea}
                  otherProps={{ style: { resize: 'none' } }}
                />
              </div>
            ))}
          </div>
        ) : null}
      </td>
      <td className={styles.PreStartQuestionAnswerCell}>
        {isWorkerScope ? (
          <div className={styles.PreStartAnswerStack}>
            {workerScopeAnswers.length > 0 ? workerScopeAnswers.map(({ participant, participantIndex, answer, fieldName, commentFieldName, flaggedResponseFieldName }) => {
              const entryKey = getCommentEntryKey(participantIndex);
              const commentVisible = Boolean(openCommentKeys[entryKey]) || hasCommentValue(answer);

              return (
                <div className={styles.PreStartAnswerRow} key={`${participant && (participant.staff_id || participant.added_person_name || "worker")}-${participantIndex}`}>
                  <span className={styles.PreStartAnswerName}>{resolveParticipantName(participant) || "Unnamed worker"}</span>
                  <div className={styles.PreStartAnswerContent}>
                    <div className={styles.PreStartAnswerBox}>
                      <CommentIconButton active={commentVisible} onClick={() => showComment(entryKey)} />
                      <AnswerCell filesPath={filesPath} questionConfig={questionConfig} answer={answer} fieldName={fieldName} flaggedResponseFieldName={flaggedResponseFieldName} formChange={formChange} expandFlaggedToFullRow photoPreviewKey={`${questionConfig && questionConfig.id ? questionConfig.id : number}-${participantIndex}`} onOpenPhotoPreview={onOpenPhotoPreview} />
                    </div>
                  </div>
                </div>
              );
            }) : (
              <span className={styles.PreStartAnswerEmpty}>No worker responses</span>
            )}
          </div>
        ) : (
          <div className={styles.PreStartAnswerRow}>
            <div className={styles.PreStartAnswerContent}>
              <div className={styles.PreStartAnswerBox}>
                <CommentIconButton active={Boolean(openCommentKeys[getCommentEntryKey('supervisor')]) || hasCommentValue(supervisorAnswer)} onClick={() => showComment(getCommentEntryKey('supervisor'))} />
                <AnswerCell filesPath={filesPath} questionConfig={questionConfig} answer={supervisorAnswer} fieldName={supervisorFieldName} flaggedResponseFieldName={supervisorFlaggedResponseFieldName} formChange={formChange} photoPreviewKey={`${questionConfig && questionConfig.id ? questionConfig.id : number}-supervisor`} onOpenPhotoPreview={onOpenPhotoPreview} />
              </div>
            </div>
          </div>
        )}
      </td>
    </tr>
  );
}